import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import QRCode from 'qrcode';
import { communityBasedProgramsContent } from '../content/educational/communityBasedProgramsContent';

const generatePDF = async (content, title, keywords) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 25;
    const contentWidth = pageWidth - (margin * 2);
    const headerHeight = 40;
    const footerHeight = 20;
    const lineSpacing = 3;
    const paragraphSpacing = 1.2;
    const bulletIndent = 12;

    // Generate QR Codes
    const qrCodeHomepage = await QRCode.toDataURL('https://causeandimpact.org');
    const qrCodeApply = await QRCode.toDataURL('https://causeandimpact.org/apply');

    // Helper function to process markdown text
    const processMarkdown = (text) => {
      let segments = [];
      const boldRegex = /\*\*(.*?)\*\*/g;
      let lastIndex = 0;
      let match;
      
      while ((match = boldRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          segments.push({
            text: text.substring(lastIndex, match.index),
            bold: false
          });
        }
        segments.push({
          text: match[1],
          bold: true
        });
        lastIndex = match.index + match[0].length;
      }
      
      if (lastIndex < text.length) {
        segments.push({
          text: text.substring(lastIndex),
          bold: false
        });
      }
      
      return segments;
    };

    // Helper function to handle bullet points and numbered lists
    const processList = (text, yPosition) => {
      const lines = text.split('\n').filter(line => line.trim() !== '');
      let currentY = yPosition;
      const bulletIndent = 12;
      const numberIndent = 8;
      
      lines.forEach(line => {
        if (line.trim() === '') return;
        
        const trimmedLine = line.trim();
        
        // Handle numbered lists with bold (e.g., "**1. Title**")
        const numberedBoldMatch = trimmedLine.match(/^\*\*(\d+\.\s*.*?)\*\*$/);
        if (numberedBoldMatch) {
          doc.setFont('helvetica', 'bold');
          doc.text(numberedBoldMatch[1], margin + numberIndent, currentY);
          currentY += 6;
          return;
        }
        
        if (trimmedLine.startsWith('-')) {
          const bulletText = trimmedLine.substring(1).trim();
          
          if (bulletText) {
            // Add bullet point
            doc.text('•', margin + bulletIndent, currentY);
            const x = margin + bulletIndent + 8;
            
            // Handle bold prefixes in bullet points (e.g., "- **Title**: Content")
            const boldPrefixMatch = bulletText.match(/^\*\*(.*?)\*\*:\s*(.*)$/);
            if (boldPrefixMatch) {
              const [, boldPart, restPart] = boldPrefixMatch;
              
              // Render bold prefix
              doc.setFont('helvetica', 'bold');
              doc.text(boldPart + ':', x, currentY);
              const boldWidth = doc.getTextWidth(boldPart + ': ');
              
              // Render rest of text
              doc.setFont('helvetica', 'normal');
              const availableWidth = contentWidth - (bulletIndent + 20) - boldWidth;
              const restLines = doc.splitTextToSize(restPart, availableWidth);
              
              if (restLines.length > 1) {
                restLines.forEach((line, index) => {
                  doc.text(line, x + boldWidth, currentY + (index * 4));
                });
                currentY += (restLines.length - 1) * 4;
              } else {
                doc.text(restPart, x + boldWidth, currentY);
              }
            } else {
              // Regular bullet point
              doc.setFont('helvetica', 'normal');
              const lines = doc.splitTextToSize(bulletText, contentWidth - (bulletIndent + 20));
              doc.text(lines, x, currentY);
              if (lines.length > 1) {
                currentY += (lines.length - 1) * 4;
              }
            }
            
            currentY += 6; // Space between bullet points
          }
        } else {
          // Regular paragraph text
          doc.setFont('helvetica', 'normal');
          const lines = doc.splitTextToSize(trimmedLine, contentWidth);
          doc.text(lines, margin, currentY);
          currentY += lines.length * 4;
        }
      });
      
      return currentY;
    };

    // Header, Footer, and CTA components
    const addHeader = () => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, 0, pageWidth, headerHeight, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      
      doc.setTextColor(37, 99, 235);
      doc.text('Cause & Impact, Inc.', (pageWidth / 2) + 0.5, 20.1, { align: 'center' });
      
      doc.setTextColor(5, 150, 105);
      doc.text('Cause & Impact, Inc.', pageWidth / 2, 20, { align: 'center' });

      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text('Starting a Nonprofit Cause never got easier!', pageWidth / 2, 30, { align: 'center' });

      doc.addImage(qrCodeHomepage, 'PNG', pageWidth - margin - 25, 10, 20, 20);
    };

    const addFooter = (pageNumber) => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      doc.text(
        'Cause & Impact, Inc. | EIN: 84-3918881 | www.causeandimpact.org',
        pageWidth / 2,
        pageHeight - 8,
        { align: 'center' }
      );
      doc.text(`Page ${pageNumber}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
    };

    const addCallToAction = (yPosition) => {
      const ctaHeight = 45;
      const qrSize = 30;
      doc.setFillColor(37, 99, 235);
      doc.roundedRect(margin, yPosition, contentWidth, ctaHeight, 5, 5, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.text('Ready to Get Started?', pageWidth / 2, yPosition + 20, { align: 'center' });

      doc.setFontSize(12);
      doc.text('Visit causeandimpact.org/apply', pageWidth / 2, yPosition + 30, { align: 'center' });

      doc.addImage(qrCodeApply, 'PNG', pageWidth - qrSize - margin - 10, yPosition + 7.5, qrSize, qrSize);
    };

    // Content generation
    const addContent = () => {
      let yPosition = headerHeight + 12;
      let pageNumber = 1;

      doc.setFontSize(22);
      doc.setTextColor(37, 99, 235);
      doc.setFont('helvetica', 'bold');
      const titleLines = doc.splitTextToSize(title, contentWidth);
      doc.text(titleLines, pageWidth / 2, yPosition + 6, { align: 'center' });
      yPosition += (titleLines.length * 7) + 12;

      content.forEach((section, index) => {
        if (yPosition + 50 > pageHeight - footerHeight - 45) {
          doc.addPage();
          pageNumber++;
          addHeader();
          addFooter(pageNumber);
          yPosition = headerHeight + 12;
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(5, 150, 105);
        doc.text(section.heading, margin, yPosition);
        yPosition += 10;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(31, 41, 55);
        
        const paragraphs = section.text.split('\n\n');
        paragraphs.forEach(paragraph => {
          if (yPosition + 20 > pageHeight - footerHeight - 45) {
            doc.addPage();
            pageNumber++;
            addHeader();
            addFooter(pageNumber);
            yPosition = headerHeight + 12;
          }
          
          yPosition = processList(paragraph, yPosition);
          yPosition += paragraphSpacing * 2;
        });

        yPosition += 8;

        if (index === content.length - 1) {
          if (yPosition + 60 > pageHeight - footerHeight) {
            doc.addPage();
            pageNumber++;
            addHeader();
            addFooter(pageNumber);
            yPosition = headerHeight + 12;
          }
          addCallToAction(pageHeight - footerHeight - 50);
        }
      });
    };

    // Generate PDF
    addHeader();
    addFooter(1);
    addContent();

    // Add metadata
    doc.setProperties({
      title: title,
      subject: 'Community Based Programs Guide',
      keywords: keywords.join(', '),
      author: 'Cause & Impact',
      creator: 'Cause & Impact'
    });

    return doc;
  } catch (error) {
    console.error('Error generating PDF:', error);
    return null;
  }
};

export const generateCommunityBasedProgramsOverview = async () => {
  try {
    const doc = await generatePDF(
      communityBasedProgramsContent.sections,
      communityBasedProgramsContent.title,
      communityBasedProgramsContent.keywords
    );
    
    if (!doc) {
      throw new Error('PDF generation failed');
    }
    
    return doc;
  } catch (error) {
    console.error('Error in generateCommunityBasedProgramsOverview:', error);
    return null;
  }
}; 